// /users/$user_id/transactions — Per-player PayPal IPN transaction history (v1-B)
// Spec: docs/plans/admin-tooling.md §8.1
//
// Shows dps_accepted rows for a single dp_buyer, newest first.
// Cursor pagination ("Load more"), desktop table / mobile card list (< 640 px).
// USD amounts: real-world PayPal USD, displayed with "$".
// NO em-dash (U+2014) anywhere.

import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { Button, ErrorChip, PageHeader } from '@awakenedlands/ui';
import { StatusPill, paymentStatusToPill } from '@/components/admin/status-pill';

// ── Types ─────────────────────────────────────────────────────────────────────

interface DpsRow {
  dp_id: number;
  dp_buyer: number;
  dp_for: number;
  dp_type: number;
  dp_time: number;
  dp_txn: string;
  payment_status: string;
  payer_email: string;
  payment_fee: number;
  payment_type: string;
  payer_status: string;
  residence_country: string;
  amount_cents: number;
}

interface TransactionsResponse {
  ok: boolean;
  rows: DpsRow[];
  next_cursor: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtUsd(cents: number): string {
  return '$' + (cents / 100).toFixed(2);
}

function fmtUtc(unixSec: number): string {
  return new Date(unixSec * 1000).toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
}

// ── Row components ─────────────────────────────────────────────────────────────

function TxnTableRow({ row }: { row: DpsRow }) {
  return (
    <tr className="hover:bg-panel-elev" data-testid={`user-txn-row-${row.dp_id}`}>
      <td className="px-3 py-2 font-mono text-xs text-ink-low">
        <Link
          to="/payments/$txn_id"
          params={{ txn_id: row.dp_txn }}
          className="text-primary underline underline-offset-2"
        >
          {row.dp_txn}
        </Link>
      </td>
      <td className="px-3 py-2 tabular-nums font-mono text-xs">
        {fmtUsd(row.amount_cents)}
      </td>
      <td className="px-3 py-2">
        <StatusPill status={paymentStatusToPill(row.payment_status)} />
      </td>
      <td className="px-3 py-2 text-xs text-ink-low font-mono break-all">{row.payer_email}</td>
      <td className="px-3 py-2 text-xs text-ink-low font-mono whitespace-nowrap tabular-nums">
        {fmtUtc(row.dp_time)}
      </td>
    </tr>
  );
}

function TxnCard({ row }: { row: DpsRow }) {
  return (
    <div
      className="rounded-sm border border-border bg-panel p-3 space-y-1.5"
      data-testid={`user-txn-card-${row.dp_id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <Link
          to="/payments/$txn_id"
          params={{ txn_id: row.dp_txn }}
          className="font-mono text-xs text-primary underline underline-offset-2 truncate"
        >
          {row.dp_txn}
        </Link>
        <StatusPill status={paymentStatusToPill(row.payment_status)} />
      </div>
      <div className="text-xs text-ink-low font-mono space-y-0.5">
        <div className="tabular-nums">{fmtUsd(row.amount_cents)}</div>
        <div className="break-all">{row.payer_email}</div>
        <div className="tabular-nums">{fmtUtc(row.dp_time)}</div>
      </div>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function UserTransactionsPage() {
  const { user_id } = useParams({ from: '/users/$user_id/transactions' });

  const [cursor, setCursor] = useState<string | null>(null);
  const [accRows, setAccRows] = useState<DpsRow[]>([]);
  const [loadMoreBusy, setLoadMoreBusy] = useState(false);

  const { data, isLoading, isError } = useQuery<TransactionsResponse>({
    queryKey: ['admin-user-transactions', user_id],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '50' });
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await apiFetch<TransactionsResponse>(
        `/admin/users/${user_id}/transactions${qs}`,
      );
      if (!res.ok) throw new Error('Failed to load transactions.');
      setAccRows(res.data.rows);
      setCursor(res.data.next_cursor);
      return res.data;
    },
  });

  async function handleLoadMore() {
    if (!cursor) return;
    setLoadMoreBusy(true);
    const params = new URLSearchParams({ limit: '50', cursor });
    const res = await apiFetch<TransactionsResponse>(
      `/admin/users/${user_id}/transactions?${params.toString()}`,
    );
    setLoadMoreBusy(false);
    if (!res.ok) return;
    setAccRows((prev) => [...prev, ...res.data.rows]);
    setCursor(res.data.next_cursor);
  }

  const displayedRows: DpsRow[] = accRows.length > 0 ? accRows : (data?.rows ?? []);
  const hasRows = displayedRows.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to="/users/$user_id"
          params={{ user_id }}
          className="text-xs text-ink-low underline underline-offset-2 hover:text-ink-high"
        >
          Back to player
        </Link>
      </div>

      <PageHeader
        title={`Payment History`}
        description={`PayPal transactions for player #${user_id}`}
      />

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-ink-low border-t-primary" />
        </div>
      )}

      {/* Error */}
      {isError && (
        <ErrorChip data-testid="user-txn-error">Failed to load transactions.</ErrorChip>
      )}

      {/* Empty state */}
      {!isLoading && !isError && !hasRows && (
        <p className="text-sm text-ink-low" data-testid="user-txn-empty">
          No transactions found for player #{user_id}.
        </p>
      )}

      {/* Results */}
      {!isLoading && !isError && hasRows && (
        <>
          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto rounded-sm border border-border">
            <table className="w-full text-sm" data-testid="user-txn-table">
              <thead className="bg-muted text-left">
                <tr>
                  <th className="px-3 py-2 font-medium text-ink-mid">Txn ID</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Amount</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Status</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Payer Email</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Date (UTC)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {displayedRows.map((row) => (
                  <TxnTableRow key={row.dp_id} row={row} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="flex flex-col gap-3 sm:hidden" data-testid="user-txn-cards">
            {displayedRows.map((row) => (
              <TxnCard key={row.dp_id} row={row} />
            ))}
          </div>

          {/* Load more */}
          {cursor && (
            <div className="flex justify-center">
              <Button
                type="button"
                variant="secondary"
                onClick={() => { void handleLoadMore(); }}
                disabled={loadMoreBusy}
                data-testid="user-txn-load-more-btn"
              >
                {loadMoreBusy ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-ink-low border-t-primary" />
                    Loading...
                  </span>
                ) : (
                  'Load more'
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
